import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BiometricCatalogService, type Category, type CategoryCreate, type CategoryUpdate, type BiometricCategory, type BiometricCategoryCreate } from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";
import { fetchAll } from "./fetchAll";

export const useCategories = () =>
  useQuery({
    queryKey: ["categories"],
    queryFn: () => fetchAll<Category>(BiometricCatalogService.getApiCategories),
    staleTime: 5 * 60_000,
  });

export const useCategory = (id: string) =>
  useQuery({
    queryKey: ["category", id],
    queryFn: async () => {
      const res = await BiometricCatalogService.getApiCategoriesById({ path: { id }, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Not found");
      return res.data as unknown as Category;
    },
    enabled: !!id,
  });

export const useCreateCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CategoryCreate) => {
      const res = await BiometricCatalogService.postApiCategories({ body: data, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as Category;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  });
};

export const useUpdateCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CategoryUpdate }) => {
      const res = await BiometricCatalogService.putApiCategoriesById({ path: { id }, body: data, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as Category;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  });
};

export const useBiometricCategories = () =>
  useQuery({
    queryKey: ["biometric-categories"],
    queryFn: () => fetchAll<BiometricCategory>(BiometricCatalogService.getApiBiometricCategories),
    staleTime: 5 * 60_000,
  });

export const useDeleteCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await BiometricCatalogService.deleteApiCategoriesById({ path: { id }, headers: getAuthHeaders() });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  });
};

export const useCreateBiometricCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: BiometricCategoryCreate) => {
      const res = await BiometricCatalogService.postApiBiometricCategories({ body: data, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as BiometricCategory;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["biometric-categories"] }),
  });
};

export const useDeleteBiometricCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
const res = await BiometricCatalogService.deleteApiBiometricCategoriesById({ path: { id }, headers: getAuthHeaders() });
      if (res.error) throw new Error("Failed to remove biometric from category");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["biometric-categories"] }),
  });
};
